import { useEffect, useRef, useState } from "react"
import {
  ActivityIndicator,
  Keyboard,
  Pressable,
  TextInput,
  TextStyle,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from "react-native"

import { Ionicons } from "@expo/vector-icons"
import { BottomSheetModal, BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import { router, useLocalSearchParams } from "expo-router"
import { KeyboardAwareScrollView } from "react-native-keyboard-controller"

import { Button, Text, TextField } from "src/components/Ignite"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { logSentryBreadcrumb } from "src/utils/crashReporting"

// Simple email validation function
const isValidEmail = (email: string): boolean => {
  if (!email) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export default function ForgotPassword() {
  const { authApi } = useAuth()
  const { theme, themed } = useAppTheme()
  const confirmationSheetRef = useRef<BottomSheetModal>(null)

  const emailInput = useRef<TextInput>(null)
  const params = useLocalSearchParams<{ email?: string }>()
  const [email, setEmail] = useState(params.email || "")
  const [touched, setTouched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const emailError =
    touched && email ? (!isValidEmail(email) ? "Please enter a valid email address" : "") : ""

  useEffect(() => {
    confirmationSheetRef.current?.present()
  }, [confirmationSheetRef])

  const handleBlur = () => {
    setTouched(true)
  }

  const onResetPassword = async () => {
    if (!isValidEmail(email)) {
      emailInput.current?.focus()
      return
    }

    try {
      logSentryBreadcrumb("password_reset_attempt")
      setIsError(false)
      setIsLoading(true)

      const response = await authApi.forgotPassword({ email })

      if (response.kind !== "ok") {
        setIsError(true)
        console.error("[onResetPassword] Failed to reset password", response)
        return
      }

      router.push({
        pathname: "/unauth/reset-password",
        params: { email },
      })
    } catch (error: any) {
      console.log("Password Reset Error", error)
      logSentryBreadcrumb("password_reset_error")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <BottomSheetModalProvider>
      <TouchableWithoutFeedback onPress={() => Keyboard.dismiss()} accessible={false}>
        <View style={themed($container)}>
          <View style={themed($header)}>
            <Pressable
              onPress={() => {
                Keyboard.dismiss()
                router.dismiss()
              }}
              style={({ pressed }) => [themed($backButton), pressed && themed($pressed)]}
            >
              <Ionicons name="arrow-back" size={24} style={themed($iconColor)} />
              <Text style={themed($backButtonText)}>Back to Login</Text>
            </Pressable>
          </View>

          <KeyboardAwareScrollView
            contentContainerStyle={themed($contentContainer)}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            <Text
              testID="forgot-password-heading"
              text="Forgot Password?"
              preset="heading"
              style={themed($heading)}
            />
            <Text style={themed($subHeading)} size="sm">
              No worries. We&apos;ll send you reset instructions if you have an account.
            </Text>

            <View style={themed($formContainer)}>
              <TextField
                autoFocus
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                label="Email"
                placeholder="Enter your email address"
                style={themed($input)}
                inputWrapperStyle={themed($inputWrapper)}
                ref={emailInput}
                value={email}
                onChangeText={setEmail}
                onBlur={handleBlur}
                helper={emailError}
                status={emailError ? "error" : undefined}
                onSubmitEditing={onResetPassword}
                returnKeyType="done"
              />

              <Button
                testID="reset-password-button"
                text="Reset Password"
                preset="sidekick"
                onPress={onResetPassword}
                disabled={isLoading || !isValidEmail(email)}
              />
            </View>

            {isLoading && (
              <View style={themed($loadingContainer)}>
                <Text style={themed($loadingText)}>Sending reset instructions...</Text>
                <ActivityIndicator color={theme.colors.primary} />
              </View>
            )}

            {isError && (
              <View style={themed($errorContainer)}>
                <Text style={themed($errorText)}>An error occurred. Please try again.</Text>
              </View>
            )}
          </KeyboardAwareScrollView>
        </View>
      </TouchableWithoutFeedback>
    </BottomSheetModalProvider>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flex: 1,
  backgroundColor: colors.background,
  padding: spacing.md,
})

const $header: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  alignItems: "center",
  paddingVertical: spacing.md,
})

const $backButton: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  padding: spacing.xs,
  paddingTop: 30,
  paddingBottom: 30,
  borderRadius: 20,
  flexDirection: "row",
  alignItems: "center",
})

const $pressed: ThemedStyle<ViewStyle> = () => ({
  opacity: 0.5,
})

const $iconColor: ThemedStyle<{ color: string }> = ({ colors }) => ({
  color: colors.text,
})

const $contentContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexGrow: 1,
  paddingHorizontal: spacing.sm,
  paddingBottom: spacing.xxl,
})

const $formContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.md,
  marginTop: spacing.xl,
})

const $heading: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginBottom: spacing.sm,
  textAlign: "center",
})

const $subHeading: ThemedStyle<TextStyle> = () => ({
  fontSize: 16,
  textAlign: "center",
  marginBottom: 24,
})

const $input: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.inputText,
})

const $inputWrapper: ThemedStyle<ViewStyle> = ({ colors }) => ({
  borderColor: colors.inputBorder,
  borderRadius: 12,
  borderWidth: 2,
})

const $backButtonText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text,
  fontFamily: typography.fonts.figtree.normal,
  fontSize: 16,
  marginLeft: 4,
})

const $loadingContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  marginTop: spacing.md,
})

const $loadingText: ThemedStyle<TextStyle> = () => ({
  marginBottom: 8,
})

const $errorContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  marginTop: spacing.md,
})

const $errorText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.error,
})
